import { AssessmentProgram, AssessmentDto, CapabilityAreas } from "@lib/ShiOneClient";
import { capabilityAreasColors } from "@features/assessments-feature/utils/assessmentsColors";
import { shouldUseTextLabels } from "@features/assessments-feature/utils/maturityScoreUtils";

const handleCapabilityName = (capability: string, program?: AssessmentProgram) => {
  // For SPR V2, use custom labels
  if (shouldUseTextLabels(program)) {
    const sprV2Labels: Record<string, string> = {
      unknown: "Unknown",
      gap: "Areas With Gaps",
      functionalAreas: "Areas Needing Improvement",
      operationalAreas: "Operational Areas",
    };

    if (sprV2Labels[capability]) {
      return sprV2Labels[capability];
    }
  }

  // For other assessments, convert camelCase to readable format
  let capabilityCamelCase = capability.replace(/([A-Z])/g, " $1").split(" ");
  return capabilityCamelCase
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const handleConvertDataToLineVisual = (
  data?: CapabilityAreas,
  program?: AssessmentProgram,
) => {
  const capabilities: CapabilityAreas = data ?? new CapabilityAreas({});

  // For SPR V2, combine scores 1 and 2 (both map to "Gap") and map to 4 display categories
  // Order from highest maturity (top) to lowest (bottom)
  if (shouldUseTextLabels(program)) {
    const gapCount = (capabilities.areasWithGaps ?? 0) + (capabilities.evaluatingAreas ?? 0);

    return [
      {
        color: capabilityAreasColors[3],
        label: "Operational Areas",
        name: "Capability Areas",
        data: capabilities.operationalAreas ?? 0,
      },
      {
        color: capabilityAreasColors[2],
        label: "Areas Needing Improvement",
        name: "Capability Areas",
        data: capabilities.functionalAreas ?? 0,
      },
      {
        color: capabilityAreasColors[1],
        label: "Areas With Gaps",
        name: "Capability Areas",
        data: gapCount,
      },
      {
        color: capabilityAreasColors[0],
        label: "Unknown",
        name: "Capability Areas",
        data: capabilities.unknown ?? 0,
      },
    ];
  }

  // For other assessments, show all capability areas
  return Object.keys(capabilities).map((area, index) => ({
    color: capabilityAreasColors[index],
    label: handleCapabilityName(area, program),
    name: "Capability Areas",
    // @ts-ignore
    data: capabilities?.[area] ?? 0,
  }));
};

export const findMostRecentAssessment = (assessments: AssessmentDto[] | undefined) => {
  return assessments?.reduce<AssessmentDto | null>((latest, current) => {
    if (!current.assessmentDate) return latest;
    if (!latest) return current;
    // @ts-ignore
    return latest.assessmentDate > current.assessmentDate ? latest : current;
  }, null);
};
